// Mock order data generator - Pure data layer
// Only responsible for generating sample data, no business logic

/**
 * Generates mock order data for testing
 * Status is determined by payment state + product statuses (calculated by business logic)
 */
export function generateMockOrders() {
  const now = Date.now();
  const orders = [];
  
  // Scenario 1: Unpaid order - all products pending
  orders.push(createOrder({
    id: 51220,
    offset: 0,
    now,
    isPaid: false,
    isPickup: true,
    productStatuses: ['PENDENTE', 'PENDENTE', 'PENDENTE']
  }));
  
  // Scenario 2: Paid, starting preparation
  orders.push(createOrder({
    id: 51221,
    offset: 1,
    now,
    isPaid: true,
    isPickup: false,
    productStatuses: ['PREPARANDO', 'PENDENTE', 'PENDENTE']
  }));
  
  // Scenario 3: Paid, mixed progress
  orders.push(createOrder({
    id: 51222,
    offset: 2,
    now,
    isPaid: true,
    isPickup: true,
    productStatuses: ['PREPARANDO', 'PREPARANDO', 'PRONTO']
  }));
  
  // Scenario 4: Paid, all ready for pickup
  orders.push(createOrder({
    id: 51223,
    offset: 3,
    now,
    isPaid: true,
    isPickup: true,
    productStatuses: ['PRONTO', 'PRONTO', 'PRONTO']
  }));
  
  // Scenario 5: Paid, all ready for delivery
  orders.push(createOrder({
    id: 51224,
    offset: 4,
    now,
    isPaid: true,
    isPickup: false,
    productStatuses: ['PRONTO', 'PRONTO', 'PRONTO']
  }));
  
  // Scenario 6: Paid, partial progress
  orders.push(createOrder({
    id: 51225,
    offset: 5,
    now,
    isPaid: true,
    isPickup: true,
    productStatuses: ['PREPARANDO', 'PRONTO', 'PENDENTE']
  }));
  
  // Scenario 7: Order with partial return
  orders.push(createOrder({
    id: 51226,
    offset: 6,
    now,
    isPaid: true,
    isPickup: false,
    productStatuses: ['PRONTO', 'PRONTO', 'PRONTO'],
    hasPartialReturn: true
  }));
  
  // Scenario 8: Order with full return
  orders.push(createOrder({
    id: 51227,
    offset: 7,
    now,
    isPaid: true,
    isPickup: true,
    productStatuses: ['PRONTO', 'PRONTO', 'PRONTO'],
    hasFullReturn: true
  }));
  
  // Scenario 9: Cancelled order
  orders.push(createOrder({
    id: 51228,
    offset: 8,
    now,
    isPaid: false,
    isPickup: true,
    productStatuses: ['PENDENTE', 'PENDENTE', 'PENDENTE'],
    forceStatus: 'CANCELADO'
  }));
  
  // Scenario 10: Completed order (delivered) without returns
  orders.push(createOrder({
    id: 51229,
    offset: 9,
    now,
    isPaid: true,
    isPickup: false,
    productStatuses: ['PRONTO', 'PRONTO', 'PRONTO'],
    forceStatus: 'CONCLUÍDO'
  }));
  
  return orders;
}

function createOrder({ id, offset, now, isPaid, isPickup, productStatuses, forceStatus, hasPartialReturn, hasFullReturn }) {
  const products = buildSampleProducts(offset, productStatuses);
  const subtotal = products.reduce((s, p) => s + p.unitPrice * p.quantity, 0);
  const shipping = isPickup ? 0 : 5;
  const total = subtotal + shipping;
  
  // Build returns array if needed
  let returns = null;
  if (hasPartialReturn) {
    returns = [
      {
        id: `RET-${id}-1`,
        createdAt: new Date(now - offset * 3000_000).toISOString(),
        amount: products[0].total, // Return first product only
        reason: 'Produto defeituoso',
        items: [{
          productId: products[0].id,
          quantity: products[0].quantity,
          name: products[0].name,
          variant: products[0].variant
        }],
        receiptUrl: `https://example.com/receipts/return_${id}_1.pdf`
      }
    ];
  } else if (hasFullReturn) {
    returns = [
      {
        id: `RET-${id}-1`,
        createdAt: new Date(now - offset * 3000_000).toISOString(),
        amount: total,
        reason: 'Cliente desistiu da compra',
        items: products.map(p => ({
          productId: p.id,
          quantity: p.quantity,
          name: p.name,
          variant: p.variant
        })),
        receiptUrl: `https://example.com/receipts/return_${id}_1.pdf`
      }
    ];
  }
  
  return {
    id: String(id),
    code: `#${id}`,
    createdAt: new Date(now - offset * 3600_000).toISOString(),
    status: forceStatus || null, // Let business logic calculate if not forced (we avoid forcing for returns)
    type: 'Mercadoria',
    payment: {
      environment: 'Presencial',
      method: 'Dinheiro',
      paidAt: isPaid ? new Date(now - offset * 3500_000).toISOString() : null,
      amountPaid: isPaid ? total : 0,
      change: isPaid ? 0.1 : 0,
      paymentCode: `PAY-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
      receiptUrl: isPaid ? `https://example.com/receipts/payment_${id}.pdf` : null
    },
    delivery: {
      method: isPickup ? 'Retirar na loja' : 'Entrega',
      maxPickupDate: new Date(now + 7 * 86400_000).toISOString()
    },
    pricing: {
      subtotal,
      shipping,
      discounts: 0,
      total
    },
    invoice: isPaid ? {
      url: `https://example.com/invoices/nota_fiscal_${id}.pdf`,
      filename: `nota_fiscal_${id}.pdf`
    } : null,
    returns,
    products
  };
}

function buildSampleProducts(seedIndex, statuses) {
  const templates = [
    {
      name: 'Coca Cola Original',
      thumbnail: 'https://via.placeholder.com/64?text=Coca',
      variant: '2 litros',
      color: 'Vermelho',
      style: 'Garrafa',
      weight: '2kg',
      type: 'Bebida',
      unitPrice: 11.4,
      quantity: 2
    },
    {
      name: 'Achocolatado em Pó Nescau',
      thumbnail: 'https://via.placeholder.com/64?text=Nescau',
      variant: 'Lata / 2kg',
      color: 'Marrom',
      style: 'Clássico',
      weight: '2kg',
      type: 'Alimento',
      unitPrice: 12.5,
      quantity: 1
    },
    {
      name: 'Arroz Branco Camil',
      thumbnail: 'https://via.placeholder.com/64?text=Arroz',
      variant: '2kg / Tradicional',
      color: 'Branco',
      style: 'Tradicional',
      weight: '2kg',
      type: 'Alimento',
      unitPrice: 11.4,
      quantity: 2
    }
  ];

  return templates.map((template, idx) => ({
    id: `p${idx + 1}-${seedIndex}`,
    ...template,
    productionStatus: statuses[idx] || 'PENDENTE',
    discountAmount: 0,
    subtotal: template.unitPrice * template.quantity,
    total: template.unitPrice * template.quantity
  }));
}
